import React, {useEffect, useState} from 'react';
import {AutoField} from 'components/forms/fields';
import {
    Typography,
    Grid,
    Button,
    Checkbox,
    FormGroup,
    FormControlLabel,
} from '@material-ui/core';
import {useFieldData} from 'components/forms/hooks';
import {useHistory} from 'react-router-dom';
import fields from './fields';
import EndSection from '../EndSection';
import {SiteUrls} from '../../../constants';
import {bootstrapOldForm} from 'components/forms/AutoSection';
import {Show} from 'components/utils/Show';
import Section from 'components/forms/Section';
import objectKeysIntersection from 'reducers/objectKeysIntersection';
import sanitizeData from 'components/forms/sections/Review/sanitizer';
import {PBold, PSingleLine} from 'components/TypographyShortcuts';
import {useFormContext, useWatch} from 'react-hook-form';

const sanitizer = data => {
    const fieldsToMask = data.hasOwnProperty('id')
        ? {...fields, id: null}
        : fields;
    const postingData = objectKeysIntersection(fieldsToMask, data);
    postingData.days_nc = `${data?.days_nc}`;
    postingData.days_inland = `${data?.days_inland}`;
    postingData.days_gl = `${data?.days_gl}`;
    return sanitizeData(postingData);
};

const AdditionHelper = ({touched, total, totalSSDays, showUploadHelp}) => {
    const history = useHistory();

    const returnToUpload = () => {
        history.push(`/forms/cg719s/upload`);
    };

    return (
        <Show condition={touched && total !== totalSSDays && !isNaN(total)}>
            <Grid style={{margin: '1em'}}>
                <Typography color='secondary'>
                    <strong>
                        You have {totalSSDays} total days, but the values you've
                        entered here add up to {total}.{' '}
                    </strong>
                </Typography>
                <Show condition={showUploadHelp}>
                    <Typography color='secondary'>
                        If the total days on your hard copy form is incorrect,
                        you can update it by lining out the entry, writing the
                        correct value and then initialling next to it. Click the
                        button below, delete your old form image and add the
                        updated form. Then click next and double check
                        everything is still correct!
                    </Typography>
                    <Button
                        color='secondary'
                        variant='contained'
                        onClick={returnToUpload}
                    >
                        Upload New Image
                    </Button>
                </Show>
            </Grid>
        </Show>
    );
};

const ControlledCheckbox = ({
    showInput,
    checkedLabel,
    uncheckedLabel,
    checked,
    checkboxOnChange,
    textOnChange,
    fieldName,
    ...props
}) => {
    return [
        <FormGroup key='checkbox'>
            <FormControlLabel
                control={
                    <Checkbox
                        {...{onChange: checkboxOnChange, checked, ...props}}
                    />
                }
                label={showInput ? checkedLabel : uncheckedLabel}
            />
        </FormGroup>,
        <AutoField
            name={fieldName}
            field={fields.days_nc}
            style={{
                display: showInput ? 'block' : 'none',
            }}
            label={''}
            onChange={textOnChange}
            key='text-input'
        />,
    ];
};

const SeaServiceBodiesCore = ({showUploadHelp}) => {
    // this is just to initialize the checkboxes
    const [_days_nc] = useFieldData('days_nc');
    const [_days_inland] = useFieldData('days_inland');
    const [_days_gl] = useFieldData('days_gl');
    // booleans control checkboxes
    const [hasNC, setHasNC] = useState(!!_days_nc);
    const [hasIN, setHasIN] = useState(!!_days_inland);
    const [hasGL, setHasGL] = useState(!!_days_gl);
    // touched is just for the warning message
    const [touched, setTouched] = useState(false);

    const [totalSSDays] = useFieldData('total_number_days');

    // we need to watch when fields change
    const {control, setValue} = useFormContext();
    const {days_nc, days_inland, days_gl} = useWatch({
        control,
        name: ['days_nc', 'days_inland', 'days_gl'],
    });
    const daysNC = parseInt(days_nc);
    const daysIN = parseInt(days_inland);
    const daysGL = parseInt(days_gl);
    // setter functions to live update the adjacent fields
    const setDaysNC = val => {
        setValue('days_nc', val, {shouldValidate: true, shouldDirty: true});
    };
    const setDaysIN = val => {
        setValue('days_inland', val, {shouldValidate: true, shouldDirty: true});
    };
    const setDaysGL = val => {
        setValue('days_gl', val, {shouldValidate: true, shouldDirty: true});
    };
    const total = daysNC + daysIN + daysGL;

    useEffect(() => {
        // if a box gets unchecked, remove that value
        if (!hasNC) {
            setDaysNC(0);
        }
        if (!hasGL) {
            setDaysGL(0);
        }
        if (!hasIN) {
            setDaysIN(0);
        }
        // if only one box is checked, set that box to be the total
        if (hasNC && !hasIN && !hasGL) {
            setDaysNC(totalSSDays);
            setDaysIN(0);
            setDaysGL(0);
        }
        if (hasIN && !hasNC && !hasGL) {
            setDaysNC(0);
            setDaysIN(totalSSDays);
            setDaysGL(0);
        }
        if (hasGL && !hasNC && !hasIN) {
            setDaysNC(0);
            setDaysIN(0);
            setDaysGL(totalSSDays);
        }
        //eslint-disable-next-line
    }, [hasNC, hasIN, hasGL]);

    const handleNCChange = e => {
        setTouched(true);
        const newVal = parseInt(e.target.value);
        const newRemain = totalSSDays - newVal;
        setDaysNC(newVal);
        // we want to update this value
        if (hasIN && !hasGL) {
            // changing NC updates Inland
            setDaysIN(newRemain);
        }
        if (!hasIN && hasGL) {
            setDaysGL(newRemain);
        }
    };
    const handleINChange = e => {
        setTouched(true);
        const newVal = e.target.value;
        const newRemain = totalSSDays - newVal;
        setDaysIN(newVal);
        // we want to update this value
        if (hasNC && !hasGL) {
            // changing NC updates Inland
            setDaysNC(newRemain);
        }
        if (!hasNC && hasGL) {
            setDaysGL(newRemain);
        }
    };
    const handleGLChange = e => {
        setTouched(true);
        const newVal = e.target.value;
        const newRemain = totalSSDays - newVal;
        setDaysGL(newVal);
        // we want to update this value
        if (hasNC && !hasIN) {
            // changing NC updates Inland
            setDaysNC(newRemain);
        }
        if (!hasNC && hasIN) {
            setDaysIN(newRemain);
        }
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Typography variant='h6' align='left'>
                    Sea Service Bodies
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <PBold align='center'>
                    Total Days Entered in Monthly Table: {totalSSDays}
                </PBold>
            </Grid>
            <Grid item xs={12}>
                <AutoField
                    field={fields.water_body_underway}
                    name='water_body_underway'
                />
            </Grid>
            <Grid item xs={12}>
                <PSingleLine>What watertypes were you sailing in?</PSingleLine>
                <PBold>Select all that apply</PBold>
            </Grid>
            <Grid item xs={12}>
                <ControlledCheckbox
                    showInput={hasNC && (hasIN || hasGL)}
                    checkedLabel='Enter Number of days served Seaward of the Boundary Line (Near Coastal)'
                    uncheckedLabel='Seaward of the Boundary Line (Near Coastal)'
                    checked={hasNC}
                    checkboxOnChange={e => setHasNC(e.target.checked)}
                    textOnChange={handleNCChange}
                    fieldName='days_nc'
                />
            </Grid>
            <Grid item xs={12}>
                <ControlledCheckbox
                    showInput={hasIN && (hasNC || hasGL)}
                    checkedLabel='Enter Number of days served Shoreward of the Boundary line (Inland)'
                    uncheckedLabel='Shoreward of the Boundary Line (Inland)'
                    checked={hasIN}
                    checkboxOnChange={e => setHasIN(e.target.checked)}
                    textOnChange={handleINChange}
                    fieldName='days_inland'
                />
            </Grid>
            <Grid item xs={12}>
                <ControlledCheckbox
                    showInput={hasGL && (hasIN || hasNC)}
                    checkedLabel='Enter Number of days served on Great Lakes'
                    uncheckedLabel='Great Lakes'
                    checked={hasGL}
                    checkboxOnChange={e => setHasGL(e.target.checked)}
                    textOnChange={handleGLChange}
                    fieldName='days_gl'
                />
            </Grid>
            <AdditionHelper
                touched={touched}
                total={total}
                totalSSDays={totalSSDays}
                showUploadHelp={showUploadHelp}
            />
        </Grid>
    );
};

const SeaServiceBodies = ({generator, ...props}) => {
    const [id] = useFieldData('id');
    const {schema, initialValues} = bootstrapOldForm(
        'Sea Service Bodies',
        fields
    );

    if (generator) {
        return (
            <Section {...props} schema={schema} initialValues={initialValues}>
                <SeaServiceBodiesCore />
                {props.children}
            </Section>
        );
    } else {
        return (
            <EndSection
                {...props}
                {...{schema, initialValues}}
                redirectUrl={SiteUrls.SEA_SERVICE_DOCS}
                method={id ? 'put' : 'post'}
                sanitizeFunction={sanitizer}
            >
                <SeaServiceBodiesCore showUploadHelp />
                {props.children}
            </EndSection>
        );
    }
};

export default SeaServiceBodies;
export {AdditionHelper};
